import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Heart, History, User, ChevronDown, ChevronRight, Tag, Pencil, Trash2, MoreVertical, Check, X, Search, Settings, ShoppingBag, Globe, SlidersHorizontal, BarChart, Layers, Palette, Compass, Image as ImageIcon, Home, HelpCircle, Bot, Images } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/apiClient";
import { pushManager } from "@/utils/pushManager";
import { useCategories } from "@/hooks/useCategories";
import { useCookie } from "@/contexts/CookieContext";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarHeader,
    SidebarProvider,
    SidebarTrigger,
    SidebarFooter,
    useSidebar,
} from "@/components/ui/sidebar";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import TutorialOverlay from '@/components/tutorial/TutorialOverlay';
import { resetTutorial } from '@/components/tutorial/TutorialOverlay';

// ─── Mobile Arrow Hint (first-visit, mobile only) ─────────────
const STORAGE_KEY = "pepite_tutorial_state";

function MobileMenuArrow() {
    const [visible, setVisible] = useState(false);
    const { showBanner } = useCookie();

    useEffect(() => {
        // Only show on mobile
        if (window.innerWidth >= 768) return;

        // Wait for cookie banner to be dismissed
        if (showBanner) {
            setVisible(false);
            return;
        }

        // Only show when tutorial is still pending
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            const state = stored ? JSON.parse(stored) : { status: "pending" };
            if (state.status !== "pending") return;
        } catch {
            return;
        }

        // Show immediately, then hide when tutorial kicks in (after ~3s)
        setVisible(true);

        // Hide after 3s (when tutorial overlay appears)
        const hideTimer = setTimeout(() => setVisible(false), 3000);

        // Also hide if user opens sidebar themselves
        const handleSidebarOpen = () => setVisible(false);
        document.addEventListener("click", handleSidebarOpen, { once: true });

        return () => {
            clearTimeout(hideTimer);
            document.removeEventListener("click", handleSidebarOpen);
        };
    }, [showBanner]);

    if (!visible) return null;

    return (
        <div
            className="mobile-menu-arrow-hint"
            aria-hidden="true"
            onClick={() => setVisible(false)}
        >
            {/* Label */}
            <span className="mobile-menu-arrow-label">Appuie ici pour voir le guide !</span>
            {/* Curved arrow SVG pointing top-left toward the button */}
            <svg
                className="mobile-menu-arrow-svg"
                viewBox="0 0 60 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M50 70 C50 40, 20 35, 8 10"
                    stroke="url(#arrowGrad)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                />
                <path
                    d="M8 10 L4 20 M8 10 L18 14"
                    stroke="url(#arrowGrad)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <defs>
                    <linearGradient id="arrowGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" />
                        <stop offset="100%" stopColor="#ec4899" />
                    </linearGradient>
                </defs>
            </svg>
        </div>
    );
}

function CategoryItem({ category, isActive, onRename, onDelete, productCount }) {
    const [isEditing, setIsEditing] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState(category);
    const { setOpenMobile } = useSidebar();

    const handleRename = () => {
        if (newCategoryName.trim() && newCategoryName.trim() !== category) {
            onRename(category, newCategoryName.trim());
        }
        setIsEditing(false);
    };

    const handleCancelEdit = () => {
        setNewCategoryName(category);
        setIsEditing(false);
    };

    const categoryUrl = createPageUrl(`categories?name=${encodeURIComponent(category)}`);

    return (
        <div className="flex items-center group">
            {isEditing ? (
                <div className="flex items-center gap-2 px-4 py-2 w-full">
                    <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault(); // Prevent form submission if in a form
                                handleRename();
                            }
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancelEdit();
                            }
                        }}
                        className="h-8 text-sm flex-1"
                    />
                    <Button variant="ghost" size="icon" onClick={handleRename}>
                        <Check className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            ) : (
                <SidebarMenuButton
                    asChild
                    className={`transition-all duration-200 rounded-lg flex-1 ${isActive
                        ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300'
                        : 'hover:bg-amber-50 dark:hover:bg-amber-950/40 text-gray-600 dark:text-gray-300'
                        }`}
                >
                    <Link to={categoryUrl} className="flex items-center gap-2 px-4 py-2" onClick={() => setOpenMobile(false)}>
                        <Tag className="w-4 h-4" />
                        <span className="text-sm truncate flex-1">{category}</span>
                        {productCount > 0 && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200 rounded-full">
                                {productCount}
                            </span>
                        )}
                    </Link>
                </SidebarMenuButton>
            )}

            {!isEditing && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsEditing(true); }}>
                            <Pencil className="mr-2 h-4 w-4" />
                            <span>Renommer</span>
                        </DropdownMenuItem>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-red-600 focus:bg-red-50 focus:text-red-700">
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    <span>Supprimer</span>
                                </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Cette action supprimera toutes les entrées de catégorie pour "{category}" de vos collections. Cela ne peut pas être annulé.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => onDelete(category)} className="bg-red-600 text-white hover:bg-red-700">
                                        Supprimer
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );
}


function LayoutContent({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [categoriesOpen, setCategoriesOpen] = useState(true);
    const { resetConsent, showBanner } = useCookie();

    const { setOpenMobile } = useSidebar();

    const handleLinkClick = () => {
        setOpenMobile(false);
    };

    // Use centralized category management
    // Use centralized category management
    const { categories: userCategories, updateCategory: renameCategoryHook, removeCategory: removeCategoryHook, isAuthenticated, user } = useCategories();
    const { data: preferences } = useQuery({
        queryKey: ['notificationPreferences'],
        queryFn: () => apiClient.notifications.getPreferences(),
        enabled: !!user?.id
    });

    // Automatic Push Subscription
    React.useEffect(() => {
        if (isAuthenticated && user?.id && preferences?.push_enabled) {
            const autoSubscribe = async () => {
                try {
                    const status = await pushManager.getStatus();
                    if (status.supported && status.permission === 'default') {
                        // Delay slightly to not overwhelm on load
                        setTimeout(() => {
                            pushManager.subscribe().catch(err => {
                                console.warn("Auto-subscription failed (likely needs user gesture or already dismissed)", err);
                            });
                        }, 2000);
                    } else if (status.supported && status.permission === 'granted' && !status.subscribed) {
                        // If granted but subscription is missing (e.g. cleared storage), re-subscribe silently
                        pushManager.subscribe().catch(() => { });
                    }
                } catch (error) {
                    console.error("Error in automatic push subscription check:", error);
                }
            };
            autoSubscribe();
        }
    }, [isAuthenticated, user?.id, preferences?.push_enabled]);

    // Wrapper to handle renaming a category group (all items with same name)
    const handleRenameCategory = async (oldName, newName) => {
        const categoriesToUpdate = userCategories.filter(c => c.category_name === oldName);
        // Sequential updates to avoid race conditions or aggressive rate limits
        for (const cat of categoriesToUpdate) {
            await renameCategoryHook(cat.id, newName);
        }
    };

    // Wrapper to handle deleting a category group
    const handleDeleteCategory = async (categoryName) => {
        const categoriesToDelete = userCategories.filter(c => c.category_name === categoryName);
        for (const cat of categoriesToDelete) {
            await removeCategoryHook(cat.id);
        }
    };

    const { data: products = [] } = useQuery({
        queryKey: ['products'],
        queryFn: () => apiClient.entities.Product.list(),
    });

    const uniqueCategories = [...new Set(userCategories.map(c => c.category_name))].filter(Boolean);

    const getCategoryProductCount = (categoryName) => {
        return userCategories.filter(c => c.category_name === categoryName).length;
    };

    const mainNavigation = [
        {
            title: "Accueil",
            url: createPageUrl("home"),
            icon: Home,
        },
        {
            title: "Découvrir",
            url: createPageUrl("swipe"),
            icon: Compass,
            tutorialId: "discover",
        },
        {
            title: "Recherche",
            url: createPageUrl("search"),
            icon: Search,
            tutorialId: "search",
        },
        {
            title: "Historique",
            url: createPageUrl("history"),
            icon: History,
            tutorialId: "history",
        },
        {
            title: "Profil",
            url: createPageUrl("profile"),
            icon: User,
        },
        {
            title: "Produits",
            url: createPageUrl("admin/products"),
            icon: ShoppingBag,
        },
        {
            title: "Sites",
            url: createPageUrl("admin/sites"),
            icon: Globe,
        },
        {
            title: "Admin",
            url: createPageUrl("admin/scraper"),
            icon: Settings,
        },
        {
            title: "Stats",
            url: createPageUrl("admin/stats"),
            icon: BarChart,
        },
        {
            title: "Algorithme",
            url: createPageUrl("admin/algorithm"),
            icon: SlidersHorizontal,
        },
        {
            title: "Config. Catégories",
            url: createPageUrl("admin/categories"),
            icon: Layers,
        },
        {
            title: "Config. Couleurs",
            url: createPageUrl("admin/colors"),
            icon: Palette,
        },
        {
            title: "Poster Studio",
            url: createPageUrl("admin/posters"),
            icon: ImageIcon,
        },
        {
            title: "Carrousel IA",
            url: createPageUrl("admin/carousel"),
            icon: Images,
        },
        {
            title: "Personal Shopper",
            url: createPageUrl("shopper"),
            icon: Bot,
        },
    ];

    // Filter out admin items for regular users, and profile for guests
    const filteredNavigation = mainNavigation.filter(item => {
        const isAdminRoute = item.url.includes('/admin') || item.title === "Stats" || item.title === "Algorithme" || item.title === "Config. Catégories" || item.title === "Config. Couleurs" || item.title === "Produits" || item.title === "Sites" || item.title === "Poster Studio" || item.title === "Carrousel IA";
        if (isAdminRoute) {
            return user?.is_admin === true;
        }
        // Hide Profil link for guests — they get a "Se connecter" link instead
        if (item.title === "Profil" && !isAuthenticated) {
            return false;
        }
        return true;
    });

    const mainContentRef = React.useRef(null);

    const handleScrollToTop = () => {
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo(0, 0);
        }
    };

    return (
        <>
            <style>{`
        :root {
          --brand-gradient: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          --accent-color: #d97706;
          --accent-hover: #b45309;
        }
      `}</style>
            <div className="h-[100dvh] overflow-hidden flex w-full bg-stone-50 dark:bg-[hsl(228,14%,6%)]">
                <Sidebar className="border-r border-amber-100 dark:border-[hsl(225,10%,16%)] bg-white/80 dark:bg-[hsl(225,14%,10%)]/90 backdrop-blur-sm">
                    <SidebarHeader className="border-b border-amber-100 dark:border-[hsl(225,10%,16%)] p-6">
                        <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden">
                                <img src="/logo-pepite.png" alt="Pépite" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <h2 className="font-bold text-2xl text-black dark:text-white">
                                    Pépite
                                </h2>

                            </div>
                        </Link>
                    </SidebarHeader>

                    <SidebarContent className="p-3">
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    {filteredNavigation.map((item) => {
                                        const isActive = location.pathname === item.url ||
                                            (location.pathname === createPageUrl("product") && location.state?.from === (item.url.replace('/', '') === 'search' ? 'search' : item.url.replace('/', '')));

                                        if (item.url === createPageUrl("matches")) {
                                            // Special handling for Matches if needed, but 'source' logic above should cover it if standardized.
                                            // Matches page url is /matches. item.url is /matches.
                                        }

                                        // Refined logic:
                                        // 1. Exact match
                                        // 2. Product page + matching source
                                        const isItemActive = location.pathname === item.url ||
                                            (location.pathname === createPageUrl("product") &&
                                                (
                                                    (item.title === "Recherche" && location.state?.from === "search") ||
                                                    (item.title === "Historique" && location.state?.from === "history") || // Assuming history will be updated later or if it used MatchCard
                                                    (item.url.includes("matches") && location.state?.from === "matches")
                                                )
                                            );

                                        return (
                                            <SidebarMenuItem key={item.title}>
                                                <SidebarMenuButton
                                                    asChild
                                                    className={`transition-all duration-200 rounded-xl mb-1 ${isItemActive
                                                        ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:from-amber-700 hover:to-amber-800 shadow-sm'
                                                        : 'hover:bg-amber-50 dark:hover:bg-amber-950/40 text-gray-700 dark:text-gray-300'
                                                        }`}
                                                >
                                                    <Link
                                                        to={item.url}
                                                        className="flex items-center gap-3 px-4 py-3"
                                                        onClick={(e) => {
                                                            handleScrollToTop();
                                                            handleLinkClick();
                                                        }}
                                                        {...(item.tutorialId ? { 'data-tutorial': item.tutorialId } : {})}
                                                    >
                                                        {item.icon && <item.icon className={`w-5 h-5 ${isItemActive ? 'text-white' : 'text-amber-600 dark:text-amber-400'}`} />}
                                                        <span className="font-medium flex-1">{item.title}</span>
                                                        {item.title === "Personal Shopper" && (
                                                            <span className="ml-auto px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-full shadow-sm">
                                                                Beta
                                                            </span>
                                                        )}
                                                    </Link>
                                                </SidebarMenuButton>
                                            </SidebarMenuItem>
                                        );
                                    })}

                                    {/* Collections Section */}
                                    <SidebarMenuItem>
                                        <Collapsible open={categoriesOpen} onOpenChange={setCategoriesOpen}>
                                            <CollapsibleTrigger asChild>
                                                <SidebarMenuButton
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-300 rounded-xl mb-1 ${(location.pathname.startsWith(createPageUrl("matches")) ||
                                                        location.pathname.startsWith(createPageUrl("categories")) ||
                                                        (location.pathname === createPageUrl("product") && (location.state?.from === "matches" || location.state?.from === "categories"))
                                                    )
                                                        ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:from-amber-700 hover:to-amber-800 shadow-sm'
                                                        : 'hover:bg-amber-50 dark:hover:bg-amber-950/40 text-gray-700 dark:text-gray-300'
                                                        }`}
                                                    data-tutorial="collections"
                                                >
                                                    <Heart className={`w-5 h-5 ${(location.pathname.startsWith(createPageUrl("matches")) ||
                                                        location.pathname.startsWith(createPageUrl("categories")) ||
                                                        (location.pathname === createPageUrl("product") && (location.state?.from === "matches" || location.state?.from === "categories"))
                                                    ) ? 'text-white' : 'text-amber-600 dark:text-amber-400'}`} />
                                                    <span className="font-medium flex-1 text-left">Collections</span>
                                                    {categoriesOpen ? (
                                                        <ChevronDown className={`w-4 h-4 ${(location.pathname.startsWith(createPageUrl("matches")) ||
                                                            location.pathname.startsWith(createPageUrl("categories")) ||
                                                            (location.pathname === createPageUrl("product") && (location.state?.from === "matches" || location.state?.from === "categories"))
                                                        ) ? 'text-white/80' : 'text-gray-500'}`} />
                                                    ) : (
                                                        <ChevronRight className={`w-4 h-4 ${(location.pathname.startsWith(createPageUrl("matches")) ||
                                                            location.pathname.startsWith(createPageUrl("categories")) ||
                                                            (location.pathname === createPageUrl("product") && (location.state?.from === "matches" || location.state?.from === "categories"))
                                                        ) ? 'text-white/80' : 'text-gray-500'}`} />
                                                    )}
                                                </SidebarMenuButton>
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <div className="ml-4 mt-1 space-y-1">
                                                    {/* Tous les matches */}
                                                    <SidebarMenuButton
                                                        asChild
                                                        className={`transition-all duration-200 rounded-lg ${location.pathname === createPageUrl("matches") || (location.pathname === createPageUrl("product") && location.state?.from === "matches")
                                                            ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300'
                                                            : 'hover:bg-amber-50 dark:hover:bg-amber-950/40 text-gray-600 dark:text-gray-300'
                                                            }`}
                                                    >
                                                        <Link
                                                            to={createPageUrl("matches")}
                                                            className="flex items-center gap-2 px-4 py-2"
                                                            onClick={(e) => {
                                                                handleScrollToTop();
                                                                handleLinkClick();
                                                            }}
                                                        >
                                                            <Heart className="w-4 h-4 fill-current" />
                                                            <span className="text-sm">Tous les matches</span>
                                                        </Link>
                                                    </SidebarMenuButton>

                                                    {/* Catégories personnalisées */}
                                                    {uniqueCategories.map((category) => {
                                                        const isCategoryActive = (location.pathname === createPageUrl("categories") &&
                                                            new URLSearchParams(location.search).get('name') === category) ||
                                                            (location.pathname === createPageUrl("product") && location.state?.from === `category:${category}`);

                                                        return (
                                                            <CategoryItem
                                                                key={category}
                                                                category={category}
                                                                isActive={isCategoryActive}
                                                                onRename={(oldName, newName) => handleRenameCategory(oldName, newName)}
                                                                onDelete={(categoryName) => handleDeleteCategory(categoryName)}
                                                                productCount={getCategoryProductCount(category)}
                                                            />
                                                        );
                                                    })}

                                                    {uniqueCategories.length === 0 && (
                                                        <div className="px-4 py-2 text-xs text-gray-400 italic">
                                                            Aucune catégorie créée
                                                        </div>
                                                    )}
                                                </div>
                                            </CollapsibleContent>
                                        </Collapsible>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </SidebarContent>

                    <SidebarFooter className="p-4 border-t border-amber-100 dark:border-[hsl(225,10%,16%)]">
                        <div className="flex flex-col gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-1.5 text-left hover:text-amber-600 dark:hover:text-amber-400 transition-colors focus:outline-none">
                                        <HelpCircle className="w-3.5 h-3.5" />
                                        Besoin d'aide ?
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-48">
                                    <DropdownMenuItem onClick={() => resetTutorial()}>
                                        <Compass className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
                                        Revoir le guide
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={createPageUrl("faq")} onClick={handleLinkClick} className="cursor-pointer">
                                            <HelpCircle className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
                                            Voir la FaQ
                                        </Link>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Link to={createPageUrl("a-propos")} className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors" onClick={handleLinkClick}>
                                À propos de nous
                            </Link>
                            <Link to={createPageUrl("mentions-legales")} className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors" onClick={handleLinkClick}>
                                Mentions Légales
                            </Link>
                            <Link to={createPageUrl("politique-confidentialite")} className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors" onClick={handleLinkClick}>
                                Politique de Confidentialité
                            </Link>
                                <button onClick={() => resetConsent()} className="text-left hover:text-amber-600 dark:hover:text-amber-400 transition-colors focus:outline-none">
                                    Gérer mes cookies
                                </button>
                            {isAuthenticated ? (
                                <button
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await apiClient.auth.logout();
                                        // Hard reload to clear all state/cache and start as fresh guest
                                        window.location.href = '/#/';
                                        window.location.reload();
                                    }}
                                    className="text-left hover:text-red-600 transition-colors focus:outline-none text-red-500 font-medium mt-2"
                                >
                                    Se déconnecter
                                </button>
                            ) : (
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white text-sm font-medium rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all shadow-sm"
                                    onClick={handleLinkClick}
                                >
                                    <User className="w-4 h-4" />
                                    Se connecter
                                </Link>
                            )}
                            <div className="mt-2 text-[10px] text-gray-400">
                                © {new Date().getFullYear()} Pépite. Tous droits réservés.
                            </div>
                        </div>
                    </SidebarFooter>
                </Sidebar>

                <main className="flex-1 flex flex-col min-w-0 safe-area-bottom">
                    <header className={`bg-white/80 dark:bg-[hsl(225,14%,10%)]/90 backdrop-blur-sm border-b border-amber-100 dark:border-[hsl(225,10%,16%)] md:hidden safe-area-top ${location.pathname === createPageUrl("swipe") ? 'px-4 py-2' : 'px-4 py-4'}`}>
                        <div className="flex items-center gap-4">
                            <SidebarTrigger className="hover:bg-amber-50 dark:hover:bg-amber-950/40 p-2 rounded-lg transition-colors" />
                            <MobileMenuArrow />
                            <h1 className={`font-bold text-black dark:text-white ${location.pathname === createPageUrl("swipe") ? 'text-lg' : 'text-xl'}`}>
                                Pépite
                            </h1>
                        </div>
                    </header>

                    <div className="flex-1 overflow-auto" ref={mainContentRef} id="main-scroll-container">
                        {children}
                    </div>
                </main>
            </div>
            <TutorialOverlay
                ready={!showBanner}
                onStart={() => {
                    if (window.innerWidth < 768) setOpenMobile(true);
                }}
            />
        </>
    );
}

export default function Layout({ children }) {
    return (
        <SidebarProvider>
            <LayoutContent>{children}</LayoutContent>
        </SidebarProvider>
    );
}